import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {DraftNode} from 'common/src/components/CLCardsMap/CardsMapContainer/CardsMapContainer.types';
import {Placement_UI} from 'common/src/modules/campaignPlacements/placements.types';
import {DTVersion} from 'common/src/modules/decisionTree/decisionTree.types';
import {DTControlMode} from 'common/src/modules/decisionTree/treeTypes';
import {DisableCampaignChangesReason, FCCampaign} from '../../campaigns.types';
import {campaignTreeInitialState} from './campaignTree.config';

const campaignTree = createSlice({
  name: 'campaignTree',
  initialState: campaignTreeInitialState,
  reducers: {
    resetCampaignTreeState: () => campaignTreeInitialState,
    updateCampaignPlacements: (state, action: PayloadAction<Placement_UI[]>) => {
      state.campaignPlacements = action.payload;
    },
    updateVersionNumber: (state, action: PayloadAction<number>) => {
      state.versionNumber = action.payload;
    },
    updateVersionId: (state, action: PayloadAction<number>) => {
      state.versionId = action.payload;
    },
    updateControlMode: (state, action: PayloadAction<DTControlMode>) => {
      state.controlMode = action.payload;
    },
    updateCampaignVersions: (state, action: PayloadAction<DTVersion[]>) => {
      state.campaignVersions = action.payload;
    },
    addDisableReason: (state, action: PayloadAction<DisableCampaignChangesReason>) => {
      state.disableCampaignChangesReasons.push(action.payload);
    },
    removeDisableReason: (state, action: PayloadAction<DisableCampaignChangesReason>) => {
      state.disableCampaignChangesReasons = state.disableCampaignChangesReasons.filter(
        (reason) => reason !== action.payload,
      );
    },
    updateIsCampaignChangedByCollaborator: (state, action: PayloadAction<boolean>) => {
      state.isCampaignChangedByCollaborator = action.payload;
    },
    updateTearsheetName: (state, action: PayloadAction<string>) => {
      state.tearsheetName = action.payload;
    },
    updateScheduledStatus: (state, action: PayloadAction<boolean>) => {
      state.isScheduled = action.payload;
    },
    updateIsCampaignOutputMenuOpen: (state, action: PayloadAction<boolean>) => {
      state.isCampaignOutputMenuOpen = action.payload;
    },
    updateIsCampaignLoading: (state, action: PayloadAction<boolean>) => {
      state.isCampaignLoading = action.payload;
    },
    updateDraftNode: (state, action: PayloadAction<DraftNode>) => {
      state.draftNode = action.payload;
    },
    updateCampaign: (state, action: PayloadAction<FCCampaign>) => {
      state.campaign = action.payload;
    },
    updateConceptCampaign: (state, action: PayloadAction<FCCampaign>) => {
      state.conceptCampaign = action.payload;
    },
    /**
     * Updates the map zoom level for the campaign tree.
     * 
     * This action persists the current zoom level to Redux, enabling zoom state
     * to be preserved when navigating between different workflow pages (Logic, Insights, etc.)
     * within the same campaign.
     * 
     * @param action.payload - The new zoom level (scale factor, e.g., 1 = 100%, 0.5 = 50%, 2 = 200%)
     * 
     * Usage:
     * - Called by CampaignWorkflowMultipleStepsContainer after user zoom operations (debounced 300ms)
     * - Default value is 1 (100% zoom)
     * - Value of 1 indicates first load; other values indicate saved zoom state
     * 
     * Related files:
     * - CampaignWorkflowMultipleStepsContainer.tsx: Dispatches this action on zoom changes
     * - CardsMapContext.tsx: Checks this value to determine if zoom-to-fit should execute
     * - Campaign workflow pages: Check this value before calling zoomToFit
     */
    updateZoomLevel: (state, action: PayloadAction<number>) => {
      state.zoomLevel = action.payload;
    },
  },
});

export const {
  resetCampaignTreeState,
  updateCampaignPlacements,
  updateVersionNumber,
  updateVersionId,
  updateControlMode,
  updateCampaignVersions,
  addDisableReason,
  removeDisableReason,
  updateIsCampaignChangedByCollaborator,
  updateTearsheetName,
  updateScheduledStatus,
  updateIsCampaignOutputMenuOpen,
  updateIsCampaignLoading,
  updateDraftNode,
  updateCampaign,
  updateConceptCampaign,
  updateZoomLevel,
} = campaignTree.actions;

export default campaignTree.reducer;
